import { defineStore } from 'pinia'
import DashboardService from '@/api/Dashboard'
import SavedSearchesService from '@/api/SavedSearches'

export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    // Dashboard list
    dashboards: [],
    dashboardsLoading: false,
    dashboardsError: null,

    // Active dashboard with widgets
    activeDashboard: null,
    activeDashboardLoading: false,
    activeDashboardError: null,

    // Browse shared dashboards
    browseDashboards: [],
    browseLoading: false,

    // Saved searches for widget data source selection
    savedSearches: [],
    savedSearchesLoading: false
  }),

  getters: {
    widgets: (state) => state.activeDashboard?.widgets || [],
    hasDashboards: (state) => state.dashboards.length > 0,

    // Get widget by ID
    getWidgetById: (state) => (widgetId) => {
      return state.activeDashboard?.widgets?.find(w => w.id === widgetId)
    }
  },

  actions: {
    /**
     * Fetch all dashboards for the current user
     */
    async fetchDashboards() {
      this.dashboardsLoading = true
      this.dashboardsError = null

      try {
        const response = await DashboardService.list()
        if (response.status === 'success') {
          this.dashboards = response.data || []
        } else {
          this.dashboardsError = response.message || 'Failed to load dashboards'
        }
      } catch (e) {
        this.dashboardsError = e.message || 'Failed to load dashboards'
      } finally {
        this.dashboardsLoading = false
      }
    },

    /**
     * Fetch a single dashboard with its widgets
     */
    async fetchDashboard(id) {
      this.activeDashboardLoading = true
      this.activeDashboardError = null

      try {
        const response = await DashboardService.get(id)
        if (response.status === 'success') {
          this.activeDashboard = response.data
        } else {
          this.activeDashboardError = response.message || 'Failed to load dashboard'
          // Keep a stub from the dashboards list so the selector still shows it
          this.activeDashboard = this.dashboards.find(d => d.id === id) || null
        }
      } catch (e) {
        this.activeDashboardError = e.message || 'Failed to load dashboard'
        this.activeDashboard = this.dashboards.find(d => d.id === id) || null
      } finally {
        this.activeDashboardLoading = false
      }
    },

    /**
     * Fetch saved searches for data source selection
     */
    async fetchSavedSearches() {
      this.savedSearchesLoading = true

      try {
        const response = await SavedSearchesService.list(0, 100)
        if (response.status === 'success') {
          this.savedSearches = response.data?.saved_searches || []
        }
      } catch (e) {
        console.error('Failed to fetch saved searches:', e)
      } finally {
        this.savedSearchesLoading = false
      }
    },

    /**
     * Create a new dashboard
     */
    async createDashboard(name, description = '') {
      const response = await DashboardService.create({ name, description })

      if (response.status === 'success') {
        await this.fetchDashboards()
        return response.data
      }

      throw new Error(response.message || 'Failed to create dashboard')
    },

    /**
     * Update dashboard metadata
     */
    async updateDashboard(id, updates) {
      const response = await DashboardService.update(id, updates)

      if (response.status === 'success') {
        await this.fetchDashboards()
        if (this.activeDashboard?.id === id) {
          // Update local state
          Object.assign(this.activeDashboard, updates)
        }
        return response.data
      }

      throw new Error(response.message || 'Failed to update dashboard')
    },

    /**
     * Delete a dashboard
     */
    async deleteDashboard(id, deleteSavedSearchIDs = []) {
      const response = await DashboardService.delete(id,
        deleteSavedSearchIDs.length > 0 ? { delete_saved_search_ids: deleteSavedSearchIDs } : {}
      )

      if (response.status === 'success') {
        if (this.activeDashboard?.id === id) {
          this.activeDashboard = null
        }
        await this.fetchDashboards()
        return true
      }

      throw new Error(response.message || 'Failed to delete dashboard')
    },

    /**
     * Get saved searches used by a dashboard
     */
    async getDashboardSavedSearches(id) {
      const response = await DashboardService.getSavedSearches(id)
      if (response.status === 'success') {
        return response.data || []
      }
      throw new Error(response.message || 'Failed to get saved searches')
    },

    /**
     * Add a widget to the active dashboard
     */
    async addWidget(widgetData) {
      if (!this.activeDashboard) {
        throw new Error('No active dashboard')
      }

      const response = await DashboardService.addWidget(
        this.activeDashboard.id,
        widgetData
      )

      if (response.status === 'success') {
        // Refresh dashboard to get updated widgets with saved search data
        await this.fetchDashboard(this.activeDashboard.id)
        return response.data
      }

      throw new Error(response.message || 'Failed to add widget')
    },

    /**
     * Update a widget's configuration
     */
    async updateWidget(widgetId, updates) {
      if (!this.activeDashboard) {
        throw new Error('No active dashboard')
      }

      const response = await DashboardService.updateWidget(
        this.activeDashboard.id,
        widgetId,
        updates
      )

      if (response.status === 'success') {
        // Refetch dashboard to get updated widget with joined saved_search data
        await this.fetchDashboard(this.activeDashboard.id)
        return response.data
      }

      throw new Error(response.message || 'Failed to update widget')
    },

    /**
     * Delete a widget
     */
    async deleteWidget(widgetId) {
      if (!this.activeDashboard) {
        throw new Error('No active dashboard')
      }

      const response = await DashboardService.deleteWidget(
        this.activeDashboard.id,
        widgetId
      )

      if (response.status === 'success') {
        // Remove from local state
        this.activeDashboard.widgets = this.activeDashboard.widgets.filter(
          w => w.id !== widgetId
        )
        return true
      }

      throw new Error(response.message || 'Failed to delete widget')
    },

    /**
     * Batch update widget layouts (for drag/drop efficiency)
     */
    async updateLayouts(layouts) {
      if (!this.activeDashboard) {
        throw new Error('No active dashboard')
      }

      const response = await DashboardService.updateLayout(
        this.activeDashboard.id,
        layouts
      )

      if (response.status === 'success') {
        // Update local state without full refetch
        layouts.forEach(({ widget_id, layout }) => {
          const widget = this.activeDashboard.widgets.find(w => w.id === widget_id)
          if (widget) {
            widget.layout = layout
          }
        })
        return true
      }

      throw new Error(response.message || 'Failed to update layouts')
    },

    /**
     * Fetch shared dashboards for browsing
     */
    async fetchBrowseDashboards() {
      this.browseLoading = true
      try {
        const response = await DashboardService.browse()
        if (response.status === 'success') {
          this.browseDashboards = response.data || []
        }
      } catch (e) {
        console.error('Failed to fetch browse dashboards:', e)
      } finally {
        this.browseLoading = false
      }
    },

    /**
     * Subscribe to a shared dashboard
     */
    async subscribeToDashboard(id) {
      const response = await DashboardService.subscribe(id)
      if (response.status === 'success') {
        await this.fetchDashboards()
        return response.data
      }
      throw new Error(response.message || 'Failed to subscribe')
    },

    /**
     * Unsubscribe from a dashboard
     */
    async unsubscribeFromDashboard(id) {
      const response = await DashboardService.unsubscribe(id)
      if (response.status === 'success') {
        if (this.activeDashboard?.id === id) {
          this.activeDashboard = null
        }
        await this.fetchDashboards()
        await this.fetchBrowseDashboards()
        return true
      }
      throw new Error(response.message || 'Failed to unsubscribe')
    },

    /**
     * Fork a shared dashboard into an editable copy
     */
    async forkDashboard(id, options = {}) {
      const response = await DashboardService.fork(id, options)
      if (response.status === 'success') {
        await this.fetchDashboards()
        return response.data
      }
      throw new Error(response.message || 'Failed to fork dashboard')
    },

    /**
     * Export a dashboard as JSON
     */
    async exportDashboard(id) {
      const response = await DashboardService.exportDashboard(id)
      if (response.status === 'success') {
        return response.data
      }
      throw new Error(response.message || 'Failed to export dashboard')
    },

    /**
     * Import a dashboard from exported JSON
     */
    async importDashboard(exportData) {
      const response = await DashboardService.importDashboard(exportData)
      if (response.status === 'success') {
        await this.fetchDashboards()
        return response.data
      }
      throw new Error(response.message || 'Failed to import dashboard')
    },

    /**
     * Set dashboard visibility level
     */
    async setVisibility(id, visibility) {
      const response = await DashboardService.setVisibility(id, visibility)
      if (response.status === 'success') {
        if (this.activeDashboard?.id === id) {
          this.activeDashboard.visibility = visibility
        }
        return response.data
      }
      throw new Error(response.message || 'Failed to update visibility')
    },

    /**
     * Clear the active dashboard
     */
    clearActiveDashboard() {
      this.activeDashboard = null
      this.activeDashboardError = null
    }
  }
})
